import { Button as AntButton, DatePicker, Modal, Select, Table, TableColumnsType } from "antd";
import _ from "lodash";
import moment from "moment";
import { FC, useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { useTranslation } from 'react-i18next';
import { createOceanStudent, getOceanStudents } from "../../../../api/oceanManagement.api";
import useAxios from "../../../../hooks/useAxios";
import useNotification from "../../../../hooks/useNotification";
import config from "../../../../lib/config";
import { IGetOceanStudents, IOceanStudent } from "../../../../models/oceanManagement.model";
import { RootState } from "../../../../store/rootReducer";
import Button from "../../../Button";
import Checkbox from "../../../Checkbox";
import ErrorResponse from "../../../ErrorResponse";
import Loader from "../../../Loader";
import Pagination from "../../../Pagination";

interface IProps {
  visible: boolean;
  setVisible: (visible: boolean) => void;
  campusCode: string | undefined;
  scheduleCode?: number;
  srtDate?: string;
  endDate?: string;
  doGetOceanStudents: () => void;
}

const AddStudentModal: FC<IProps> = ({
  visible,
  setVisible,
  campusCode,
  scheduleCode,
  srtDate,
  endDate,
  doGetOceanStudents,
}) => {
  const { t } = useTranslation();
  const { serverDate } = useSelector((state: RootState) => state.serverDate);  
  const { infoNotification, successNotification } = useNotification();
  const { data: res, isLoading, error, execute } = useAxios<IGetOceanStudents>();
  const { data: submitRes, isLoading: submitIsLoading, error: submitError, execute: submitExecute } = useAxios();

  const [eduStepCode, setEduStepCode] = useState('EM');
  const [data, setData] = useState<IOceanStudent[]>([]);
  const [pageNo, setPageNo] = useState(1);
  const [examDate, setExamDate] = useState('');
  const [name, setName] = useState('');
  const [isSelectAll, setIsSelectAll] = useState(false);
  const [memberCodes, setMemberCodes] = useState<string[]>([]);
  const [irregularEndDate, setIrregularEndDate] = useState('');

  useEffect(() => {
    if (serverDate) {
      setExamDate(moment(serverDate).subtract(1, 'years').format('YYYYMMDD'));
    }
  }, [serverDate]);

  useEffect(() => {
    if (campusCode && examDate && eduStepCode) {
      execute(getOceanStudents(campusCode, {
        eduStepCode,
        examDate,
        pageNo: 1,
        perPage: 9999999,
      }));
    }
  }, [campusCode, examDate, eduStepCode]);

  useEffect(() => {
    if (res) {
      onSearch();
    }
  }, [res]);

  useEffect(() => {
    if (submitRes) {
      doGetOceanStudents();
      successNotification(t('common.message.save1'));
      onClose();
    }
  }, [submitRes]);

  useEffect(() => {
    if (serverDate) {
      setIrregularEndDate(moment(serverDate).add(30, 'days').format('YYYYMMDD'));
    }
  }, [serverDate]);

  const onSearch = () => {
    let newData = _.cloneDeep(res?.data);

    if (examDate) {
      newData = newData?.filter(item => !item.testCompleteDate || moment(item.testCompleteDate).isBefore(examDate));
    }
    if (name) {
      newData = newData?.filter(item => item.memKorName.includes(name));
    }

    setData(newData || []);
    setIsSelectAll(false);
    setMemberCodes([]);
  }

  const onSelectAll = () => {
    if (!isSelectAll) {
      setMemberCodes(data.map(item => item.memberCode));
    } else {
      setMemberCodes([]);
    }
    setIsSelectAll(!isSelectAll);
  }

  const onSelect = (value: string) => {
    if (memberCodes.includes(value)) {
      setMemberCodes(memberCodes.filter(item => item !== value));
      setIsSelectAll(false);
    } else {
      setMemberCodes([...memberCodes, value]);
    }
  }

  const onSubmit = () => {
    if (!memberCodes.length) {
      return infoNotification(t('class.ocean.message6'));
    }
    if (!scheduleCode && !irregularEndDate) {
      return infoNotification(t('class.ocean.message7'));
    }

    const req: any = [];
    memberCodes.forEach(memberCode => {
      req.push({
        eduStepCode,
        memberCode,
        scheduleCode,
        srtDate: srtDate || moment(serverDate).format('YYYYMMDD'),
        endDate: endDate || irregularEndDate
      });
    });

    submitExecute(createOceanStudent(req))
  }

  const onClose = () => {
    setExamDate('');
    setName('');
    setIsSelectAll(false);
    setMemberCodes([]);
    setVisible(false);
  }

  const columns: TableColumnsType<IOceanStudent> = [
    {
      title: (
        <Checkbox
          checked={isSelectAll}
          onClickCapture={() => onSelectAll()}
          text=""
        />
      ),
      dataIndex: 'memberCode',
      key: 'memberCode',
      align: 'center',
      render: value => (
        <Checkbox
          checked={memberCodes.includes(value)}
          onClickCapture={() => onSelect(value)}
          text=""
        />
      ),
    },
    {
      title: t('class.ocean.column8'),
      dataIndex: 'memKorName',
      key: 'memKorName',
      align: 'center',
    },
    {
      title: t('class.ocean.column9'),
      dataIndex: 'className',
      key: 'className',
      align: 'center',
    },
    {
      title: t('class.ocean.column10'),
      dataIndex: 'testCompleteDate',
      key: 'testCompleteDate',
      align: 'center',
      render: value => value ? moment(value).format('YYYY.MM.DD') : 'N/A',
    },
  ];

  return (
    <>
      {(isLoading || submitIsLoading) ? <Loader /> : null}
      <ErrorResponse error={error} />
      <ErrorResponse error={submitError} />

      <Modal
        title={t('class.ocean.button3')}
        visible={visible}
        onCancel={onClose}
        footer={[
          <div className='comm-btn__area justify-content-center'>	
            <AntButton
              className='btn-basic pink'
              onClick={onSubmit}
            >
              {t('common.button.add')}
            </AntButton>
            <AntButton
              onClick={onClose}
              className='btn-basic gray'
            >
              {t('common.button.cancel')}
            </AntButton>
          </div>
        ]}
        width={530}
      >
        <div className='comm-table__area rowType'>
          <table>
            <tbody>
              <tr>
                <th>{t('class.class.27')}</th>
                <td colSpan={4}>
                  <Select
                    className="w-150p"
                    value={eduStepCode} 
                    onChange={value => setEduStepCode(value)}
                  >
                    <Select.Option value="EM">OCEAN</Select.Option>
                    <Select.Option value="MD">Prediger</Select.Option>
                  </Select>
                </td>
              </tr>
              <tr>
                <th>{t('class.ocean.column10')}</th>
                <td>
                  <div className="date-picker__area">
                    <DatePicker
                      value={examDate ? moment(examDate) : undefined}
                      onChange={(e) => {
                        if (e === null) setExamDate('');
                        else setExamDate(moment(e).format('YYYYMMDD'));
                      }}
                    />
                  </div>
                </td>
                <th>{t('class.ocean.column8')}</th>
                <td>
                  <input
                    type="text"
                    className="form-control input-text w-90p"
                    placeholder={t('common.text.input')}
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    onKeyPress={(e) => {
                      if (e.key === 'Enter') onSearch();
                    }}
                  />
                </td>
                <td>
                  <div className="comm-btn__area">
                    <Button
                      className="btn-basic small black"
                      onClick={onSearch}
                      text={t('common.button.search')}
                    />
                  </div>
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        <div className="comm-bx__space">
          <div className="comm-btn__area right">
            {`* ${t('class.ocean.text1')}`}
          </div>
        </div>
        <div className='comm-table__area mt-0'>
          <Table
            columns={columns}
            dataSource={data}
            pagination={{
              current: pageNo,
              pageSize: config.miniPageSize,
            }}
          />
        </div>
        <div className='comm-bx__space'>
          <div />
          <div className="common-paging center">
            <Pagination
              count={data.length}
              page={pageNo}
              setPage={setPageNo}
              mini
            />
          </div>
        </div>
        {!scheduleCode && (
          <div className="mt-4 flex flex-justify-center">
            <div className="v-line mr-1">{t('class.ocean.column11')}</div>
            <div className="date-picker__area">
              <DatePicker
                value={irregularEndDate ? moment(irregularEndDate) : undefined}
                onChange={(e) => {
                  if (e === null) setIrregularEndDate('');
                  else setIrregularEndDate(moment(e).format('YYYYMMDD'));
                }}
              />
            </div>
          </div>
        )}
      </Modal>
    </>
  );
}

export default AddStudentModal;
